/**
 * Opening a quick link on an iPhone or iPad.
 *
 * A home-screen web app on iOS has no Google cookies, and the in-app browser it
 * opens for `target="_blank"` puts a sign-in wall in front of every Google page.
 * The installed app is the right destination, and a custom URL scheme is the one
 * way to reach it that works from a standalone web app and from Safari alike.
 *
 * A scheme is a navigation, so a missing app answers with nothing (Safari shows
 * an alert). The fallback is a timer: if this page is still on screen when it
 * fires, the app did not take over, and the web page opens instead. Three things
 * say the app took over: a leave event (visibilitychange, pagehide, blur), the
 * page being hidden when the timer fires, or the timer firing late, which is
 * what a suspended page looks like once it is back in front.
 *
 * Everything the browser provides comes in through `env`, so the decision is
 * exercised in node with no DOM.
 */
export interface OpenableLink {
  url: string | null;
  ios?: string;
}

export interface OpenEnv {
  /** iPhone, iPad or iPod. */
  apple: boolean;
  navigate: (url: string) => void;
  hidden: () => boolean;
  /** Subscribe to the page leaving the screen; returns the unsubscribe. */
  onLeave: (cb: () => void) => () => void;
  now: () => number;
  after: (ms: number, cb: () => void) => void;
}

/** How long the app gets to take the screen before the web page opens instead. */
export const APP_GRACE_MS = 1200;
/** A timer this much late was suspended, and a suspended page had an app in front of it. */
export const LATE_MS = 800;

/**
 * iPadOS asks for desktop sites by default and calls itself a Mac in the user
 * agent; the touch points give it away. A Mac with a trackpad reports none.
 */
export function isApplePlatform(nav: { userAgent: string; platform?: string; maxTouchPoints?: number }): boolean {
  if (/\b(iPhone|iPad|iPod)\b/.test(nav.userAgent)) return true;
  return nav.platform === "MacIntel" && (nav.maxTouchPoints ?? 0) > 1;
}

/** Whether the link is shown on this platform: an iOS-only tool hides everywhere else. */
export function showsOn(link: OpenableLink, apple: boolean): boolean {
  return Boolean(link.url) || (apple && Boolean(link.ios));
}

/**
 * Try the app. Returns true when it took the click, so the caller prevents the
 * anchor's default; false means the anchor opens the page as it always did.
 */
export function tryApp(link: OpenableLink, env: OpenEnv): boolean {
  if (!env.apple || !link.ios) return false;
  const start = env.now();
  let left = false;
  const off = env.onLeave(() => { left = true; });
  env.navigate(link.ios);
  env.after(APP_GRACE_MS, () => {
    off();
    if (left || env.hidden() || env.now() - start > APP_GRACE_MS + LATE_MS) return;
    if (link.url) env.navigate(link.url);
  });
  return true;
}
